import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ModalUsuarios } from './ModalUsuarios';
import { C4UsuariosSistema } from '../../../../../shared/services/api/c4-model/SistemasService';
import { C4ModelUsuariosService } from '../../../../../shared/services/api/c4-model/UsuariosService';
import { Skeleton } from '@mui/material';
import { EnumContainerTipo } from '../../../../../shared/enums';


type Row = {
  id: string;
  usuarioId: string;
  usuarioNome: string;
  container: string;
  acao: string;
  meioAcesso: string;
};

type Props = {
  initialRows?: C4UsuariosSistema[];
  onUsuariosChange?: (usuarios: C4UsuariosSistema[]) => void;
  isLoading?: boolean;
};

const convertToRows = (usuariosSistema: C4UsuariosSistema[]): Row[] => {
  return usuariosSistema.map((item) => {
    const containerName =
      typeof item.container === 'number'
        ? // Enum numeric reverse lookup
        (EnumContainerTipo as any)[item.container] ?? String(item.container)
        : String(item.container);

    return {
      id: item.id,
      usuarioId: item.usuarioId,
      usuarioNome: item.usuario?.nome || `Usuário ${item.usuarioId}`,
      container: containerName,
      acao: item.acao,
      meioAcesso: item.meioAcesso,
    };
  });
};

const convertToUsuariosSistema = (rows: Row[]): C4UsuariosSistema[] => {
  return rows.map((row) => ({
    id: row.id,
    sistemaId: '', // será preenchido no backend
    usuarioId: row.usuarioId,
    container: row.container as any, // EnumContainerTipo
    acao: row.acao,
    meioAcesso: row.meioAcesso,
    usuario: {
      nome: row.usuarioNome,
      descricao: '',
      id: row.usuarioId,
    },
  }));
};

export const ListaUsuarios: React.FC<Props> = ({
  initialRows = [],
  onUsuariosChange,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const [usuariosMap, setUsuariosMap] = useState<Map<string, string>>(
    new Map()
  );


  // Carregar mapeamento de usuários
  useEffect(() => {
    C4ModelUsuariosService.getAll('1', 100, 0).then((data) => {
      if (data && !(data instanceof Error)) {
        const userMap = new Map<string, string>();
        data.data.forEach((user) => {
          userMap.set(user.id as string, user.nome);
        });
        setUsuariosMap(userMap);
      }
    });
  }, []);

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      // When no initial rows are provided, start with empty array
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setEditingRow(row);
    setOpenModal(true);
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onUsuariosChange) {
      onUsuariosChange(convertToUsuariosSistema(newRows));
    }
  };

  const handleOpen = () => {
    // clear editing when opening for a new entry
    setEditingRow(null);
    setOpenModal(true);
  };
  const handleClose = () => setOpenModal(false);

  const handleSave = (values: any) => {
    const usuarioNome =
      usuariosMap.get(values.usuarioId) || `Usuário ${values.usuarioId}`;

    let newRows: Row[];
    if (values.id) {
      newRows = rows.map((r) =>
        r.id === values.id
          ? {
            ...r,
            usuarioId: values.usuarioId || r.usuarioId,
            usuarioNome: usuarioNome,
            container: values.containerId || r.container,
            acao: values.acao || r.acao,
            meioAcesso: values.meioAcesso || r.meioAcesso,
          }
          : r
      );
    } else {
      const newRow: Row = {
        id: String(Date.now()),
        usuarioId: values.usuarioId || '',
        usuarioNome: usuarioNome,
        container: values.containerId || 'N/A',
        acao: values.acao || 'Acesso',
        meioAcesso: values.meioAcesso || 'Desconhecido',
      };
      newRows = [newRow, ...rows];
    }
    setRows(newRows);
    if (onUsuariosChange) {
      onUsuariosChange(convertToUsuariosSistema(newRows));
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleOpen}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}
      <ModalUsuarios
        open={openModal}
        onClose={() => {
          handleClose();
          setEditingRow(null);
        }}
        onSave={(v) => {
          handleSave(v);
          handleClose();
          setEditingRow(null);
        }}
        initialValues={
          editingRow
            ? {
              id: editingRow.id,
              usuarioId: editingRow.usuarioId,
              containerId: editingRow.container, // Este campo agora conterá o valor do enum (ex: 'FRONTEND', 'BACKEND')
              acao: editingRow.acao,
              meioAcesso: editingRow.meioAcesso,
            }
            : undefined
        }
      />
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Container</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Meio de acesso</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3].map((n) => (
                  <TableRow key={n}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={150} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={120} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={130} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhum usuário cadastrado
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(row.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.usuarioNome}</TableCell>
                  <TableCell>{row.container}</TableCell>
                  <TableCell>{row.acao}</TableCell>
                  <TableCell>{row.meioAcesso}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
